import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Button } from "../components/Button";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

/**
 * Switches between sibling views within the same region, built on Radix Tabs. Compose it
 * from `Tabs` (root, controlled via `value`/`defaultValue`), `TabsList`, `TabsTrigger`,
 * and `TabsContent`, pairing each trigger with a content panel sharing the same `value`.
 * Use it to organize related content into peer views toggled without leaving the page;
 * for pill-styled, sidebar-oriented section switching, use `NavTabPill` instead.
 *
 * @summary Tabbed view switcher.
 */
const Tabs = TabsPrimitive.Root;
const tabsTriggerVariants = cva(["relative", "after:absolute after:bottom-[-10px] after:left-1/2 after:h-[2px]", "after:w-full after:-translate-x-1/2", "after:bg-foreground after:opacity-0 data-[state=active]:after:opacity-100"], {
  variants: {
    variant: {
      ghost: "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-primary-900",
      primary: "",
      highlight: "",
      "highlight-secondary": "",
      "highlight-ghost": "",
      warning: "",
      "warning-secondary": "",
      "warning-ghost": "",
      outline: "",
      "ghost-secondary": ""
    }
  }
});
const tabsListVariants = cva("flex h-11 gap-2 w-full", {
  variants: {
    border: {
      true: "border-b border-border"
    }
  },
  defaultVariants: {
    border: true
  }
});
const TabsList = React.forwardRef(({
  className,
  border,
  ...props
}, ref) => /*#__PURE__*/React.createElement(ScrollArea, null, /*#__PURE__*/React.createElement(TabsPrimitive.List, {
  ref: ref,
  className: cn(tabsListVariants({
    border
  }), className),
  ...props
}), /*#__PURE__*/React.createElement(ScrollBar, {
  orientation: "horizontal",
  className: "hidden"
})));
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * A single tab button rendered as a Sparkle `Button`, with a `label`, optional `icon`,
 * and a `tooltip` — always provide the tooltip for icon-only triggers so they remain
 * identifiable. Supports link navigation props (`href`, `target`, ...), counters, and a
 * trailing `iconRight` element such as a Chip.
 *
 * @summary Tab trigger button.
 */
const TabsTrigger = React.forwardRef(({
  className,
  label,
  tooltip,
  icon,
  iconRight,
  href,
  target,
  rel,
  replace,
  shallow,
  disabled,
  variant = "ghost",
  isCounter = false,
  counterValue,
  ...props
}, ref) => {
  return /*#__PURE__*/React.createElement(TabsPrimitive.Trigger, {
    ref: ref,
    className: cn("disabled:pointer-events-none", className),
    disabled: disabled,
    asChild: true,
    ...props
  }, /*#__PURE__*/React.createElement(Button, {
    variant: variant,
    size: "sm",
    ...(label !== undefined ? {
      label,
      tooltip
    } : {
      tooltip
    }),
    icon: icon,
    iconRight: iconRight,
    disabled: disabled,
    href: href,
    target: target,
    rel: rel,
    replace: replace,
    shallow: shallow,
    isCounter: isCounter,
    counterValue: counterValue,
    className: cn(tabsTriggerVariants({
      variant: variant !== null && variant !== void 0 ? variant : undefined
    }), className)
  }));
});
TabsTrigger.displayName = "TabsTrigger";
const TabsContent = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(TabsPrimitive.Content, {
  ref: ref,
  className: cn("contents ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2", className),
  ...props
}));
TabsContent.displayName = TabsPrimitive.Content.displayName;
export { Tabs, TabsContent, TabsList, TabsTrigger };
//# sourceMappingURL=Tabs.js.map